import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useInViewRef } from 'rooks';

// Material UI
import AddCircleIcon from '@material-ui/icons/AddCircleTwoTone';
import CheckCircleIcon from '@material-ui/icons/CheckCircleTwoTone';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Link from 'components/Link';
import Tooltip from '@material-ui/core/Tooltip';

// app modules: hooks
import useFeatureToggle from 'hooks/useFeatureToggle';

// app modules: services
import route from 'services/route';
import { isApp } from 'services/environment';
import { showHelpArticle, ARTICLES } from 'services/intercom';

// assets
import CssAlacarteStatusIndicator from './AlacarteStatusIndicator.scss';

function AlacarteStatusIndicator(props) {
  const { available, className, style, diameter, isbn, signature_ } = props;

  useStyles(CssAlacarteStatusIndicator);
  const [appTooltipOpen, setAppTooltipOpen] = useState(false);
  const [ref, inView] = useInViewRef();
  const owned = useSelector(state => state.user.owned_books.includes(isbn));
  const [alacarteEnabled] = useFeatureToggle('alacarte');
  const tip = owned
    ? 'You own this book'
    : 'Add this book to ckbk for a one-off payment of the eBook price';
  const app = isApp();

  const handleAddButtonClickInApp = () => {
    setAppTooltipOpen(true);
  };

  const handleAddButtonTooltipClose = () => {
    setAppTooltipOpen(false);
  };

  useEffect(
    () => {
      if (!inView) {
        setAppTooltipOpen(false);
      }
    },
    [inView],
  );

  if (alacarteEnabled) {
    let content;
    if (available) {
      if (app) {
        content = (
          <ClickAwayListener touchEvent="onTouchStart" onClickAway={handleAddButtonTooltipClose}>
            <Tooltip
              ref={ref}
              open={appTooltipOpen}
              onClose={handleAddButtonTooltipClose}
              arrow
              interactive
              title={
                <p>
                  Visit app.ckbk.com to purchase (<button
                    className="link link--energized"
                    onClick={() => showHelpArticle(ARTICLES.accessingAlacarte)}
                  >
                    more info
                  </button>)
                </p>
              }
              placement="top"
              disableFocusListener
              disableHoverListener
              disableTouchListener
              enterDelay={500}
              enterNextDelay={500}
              id="alc-status-tooltip"
            >
              {owned ? (
                <CheckCircleIcon style={{ width: diameter, height: diameter }} />
              ) : (
                <button onClick={handleAddButtonClickInApp}>
                  <AddCircleIcon style={{ width: diameter, height: diameter }} />
                </button>
              )}
            </Tooltip>
          </ClickAwayListener>
        );
      } else {
        content = (
          <Tooltip arrow title={tip} enterDelay={500} enterNextDelay={500} placement="top">
            {owned ? (
              <CheckCircleIcon style={{ width: diameter, height: diameter }} />
            ) : (
              <Link to={route.alacartePurchase(isbn, signature_)}>
                <AddCircleIcon style={{ width: diameter, height: diameter }} />
              </Link>
            )}
          </Tooltip>
        );
      }
    } else if (owned) {
      // Not available for sale, but they own it already
      content = (
        <Tooltip arrow title={tip} enterDelay={500} enterNextDelay={500} placement="top">
          <CheckCircleIcon style={{ width: diameter, height: diameter }} />
        </Tooltip>
      );
    } else {
      // Not available and not owned - hide the icon
      return null;
    }
    return (
      <div
        style={style}
        className={classNames('alc-status-indicator', className, {
          'alc-status-indicator--owned': owned,
          'alc-status-indicator--buy': !owned,
        })}
      >
        {content}
      </div>
    );
  }
  return null;
}

AlacarteStatusIndicator.propTypes = {
  available: PropTypes.bool,
  className: PropTypes.string,
  diameter: PropTypes.number,
  isbn: PropTypes.string.isRequired,
  signature_: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
};

AlacarteStatusIndicator.defaultProps = {
  available: true,
  className: null,
  diameter: 32.5,
  signature_: null,
  style: null,
};

AlacarteStatusIndicator.ICON_WIDTH = 32.5;

export default AlacarteStatusIndicator;
